import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Search, CheckCircle, AlertTriangle, Clock,
  MessageSquare, Send, ChevronDown, ChevronUp, RotateCcw,
} from "lucide-react";
import { addNoteMessage } from "../../services/teamService";

function getAgeHours(note) {
  const created = note.createdAt?.toDate ? note.createdAt.toDate() : new Date(note.createdAt || note.date || 0);
  return (Date.now() - created.getTime()) / (1000 * 60 * 60);
}

function AgeBadge({ hours }) {
  if (hours < 24) return null;
  const days = Math.floor(hours / 24);
  const color = days >= 3 ? "#fc8181" : days >= 1 ? "#ecc94b" : "#718096";
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 6, background: `${color}15`, color, marginLeft: 4 }}>
      {days}d unsigned
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    signed: { bg: "rgba(104,211,145,0.1)", color: "#68d391", label: "✓ Signed" },
    submitted: { bg: "rgba(236,201,75,0.1)", color: "#ecc94b", label: "Pending" },
    "revision-requested": { bg: "rgba(246,173,85,0.15)", color: "#f6ad55", label: "⟲ Revision Requested" },
    draft: { bg: "rgba(99,179,237,0.08)", color: "#718096", label: "Draft" },
  };
  const s = styles[status] || styles.draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function MessageThread({ thread, noteId, supervisorName }) {
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    await addNoteMessage(noteId, newMessage.trim(), supervisorName, "supervisor");
    setNewMessage("");
    setSending(false);
  };

  if (!thread || thread.length === 0) return null;

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.04)" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#63b3ed", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
        <MessageSquare size={10} style={{ display: "inline", marginRight: 4 }} /> Thread ({thread.length})
      </div>
      {thread.map((msg, i) => (
        <div key={msg.id || i} style={{
          padding: "6px 10px", borderRadius: 8, marginBottom: 4,
          background: msg.role === "supervisor" ? "rgba(49,130,206,0.06)" : "rgba(79,209,197,0.06)",
          borderLeft: `2px solid ${msg.role === "supervisor" ? "#3182ce" : "#4fd1c5"}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: msg.role === "supervisor" ? "#63b3ed" : "#4fd1c5" }}>
              {msg.from}
              {msg.type === "revision-request" && <span style={{ color: "#f6ad55", marginLeft: 4 }}>• Revision Request</span>}
              {msg.type === "signed" && <span style={{ color: "#68d391", marginLeft: 4 }}>• Signed</span>}
              {msg.type === "resubmission" && <span style={{ color: "#4fd1c5", marginLeft: 4 }}>• Resubmitted</span>}
            </span>
            <span style={{ fontSize: 9, color: "#4a5568" }}>
              {new Date(msg.timestamp).toLocaleDateString()} {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#a0aec0", lineHeight: 1.4 }}>{msg.text}</div>
        </div>
      ))}
      {/* Reply input */}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input
          style={{ flex: 1, padding: "6px 10px", borderRadius: 8, background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 11, outline: "none" }}
          placeholder="Add a message..."
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSend()}
        />
        <button onClick={handleSend} disabled={sending || !newMessage.trim()} style={{
          padding: "6px 10px", borderRadius: 8, background: "rgba(49,130,206,0.2)", color: "#63b3ed",
          border: "none", cursor: "pointer", opacity: sending || !newMessage.trim() ? 0.4 : 1,
        }}>
          <Send size={12} />
        </button>
      </div>
    </div>
  );
}

export default function NotesTab({
  C, allNotes, submittedNotes, draftNotes, pendingReview, safetyAlerts,
  handleSignNote, setRevisionNote, supervisorName,
}) {
  const navigate = useNavigate();
  const [noteFilter, setNoteFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNote, setExpandedNote] = useState(null);

  // Revision requested notes
  const revisionNotes = useMemo(() => allNotes.filter(n => n.status === "revision-requested"), [allNotes]);

  const filteredNotes = useMemo(() => {
    let filtered = allNotes;
    if (noteFilter === "submitted") filtered = submittedNotes;
    else if (noteFilter === "drafts") filtered = draftNotes;
    else if (noteFilter === "pending") filtered = pendingReview;
    else if (noteFilter === "flagged") filtered = safetyAlerts;
    else if (noteFilter === "revision") filtered = revisionNotes;
    if (searchQuery) {
      filtered = filtered.filter(n =>
        (n.individualName || n.agentName || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered;
  }, [allNotes, noteFilter, searchQuery, submittedNotes, draftNotes, pendingReview, safetyAlerts, revisionNotes]);

  // Unsigned aging stats
  const unsignedAging = useMemo(() => {
    const unsigned = allNotes.filter(n => n.status === "submitted" && !n.supervisorReviewed);
    const over24 = unsigned.filter(n => getAgeHours(n) >= 24).length;
    const over48 = unsigned.filter(n => getAgeHours(n) >= 48).length;
    return { total: unsigned.length, over24, over48 };
  }, [allNotes]);

  return (
    <div>
      {/* Unsigned aging warning */}
      {unsignedAging.over24 > 0 && (
        <div style={{
          ...C.card, marginBottom: 12, padding: 12,
          background: unsignedAging.over48 > 0 ? "rgba(252,129,129,0.04)" : "rgba(236,201,75,0.04)",
          border: `1px solid ${unsignedAging.over48 > 0 ? "rgba(252,129,129,0.15)" : "rgba(236,201,75,0.15)"}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={14} style={{ color: unsignedAging.over48 > 0 ? "#fc8181" : "#ecc94b" }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: unsignedAging.over48 > 0 ? "#fc8181" : "#ecc94b" }}>
                {unsignedAging.over24} note{unsignedAging.over24 !== 1 ? "s" : ""} unsigned 24+ hours
              </div>
              {unsignedAging.over48 > 0 && (
                <div style={{ fontSize: 10, color: "#fc8181", marginTop: 2 }}>
                  {unsignedAging.over48} over 48 hours — cannot be billed until co-signed
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "all", label: "All", count: allNotes.length },
          { key: "pending", label: "Pending", count: pendingReview.length },
          { key: "revision", label: "Revisions", count: revisionNotes.length, highlight: revisionNotes.length > 0 },
          { key: "submitted", label: "Submitted", count: submittedNotes.length },
          { key: "flagged", label: "Flagged", count: safetyAlerts.length },
          { key: "drafts", label: "Drafts", count: draftNotes.length },
        ].map(f => (
          <button key={f.key} onClick={() => setNoteFilter(f.key)} style={{
            padding: "5px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700,
            background: noteFilter === f.key ? "rgba(99,179,237,0.15)" : f.highlight ? "rgba(246,173,85,0.08)" : "rgba(15,23,42,0.5)",
            color: noteFilter === f.key ? "#63b3ed" : f.highlight ? "#f6ad55" : "#718096",
            border: `1px solid ${noteFilter === f.key ? "rgba(99,179,237,0.25)" : f.highlight ? "rgba(246,173,85,0.15)" : "rgba(99,179,237,0.06)"}`,
            cursor: "pointer",
          }}>{f.label} ({f.count})</button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#4a5568" }} />
        <input style={{ width: "100%", padding: "10px 14px 10px 38px", borderRadius: 12, background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 13, outline: "none" }}
          placeholder="Search by individual or agent name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
      </div>

      {/* Notes */}
      {filteredNotes.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <FileText size={32} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>No notes match this filter</div>
        </div>
      ) : (
        filteredNotes.map(note => {
          const ageHrs = getAgeHours(note);
          const isExpanded = expandedNote === note.id;
          const hasThread = note.messageThread && note.messageThread.length > 0;

          return (
            <div key={note.id} style={{
              ...C.card,
              borderLeft: note.status === "revision-requested" ? "3px solid #f6ad55"
                : note.status === "signed" ? "3px solid #68d391"
                : note.hasPriorityFlags ? "3px solid #fc8181"
                : !note.supervisorReviewed && ageHrs >= 48 ? "3px solid #fc8181"
                : !note.supervisorReviewed && ageHrs >= 24 ? "3px solid #ecc94b"
                : "3px solid transparent",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 800 }}>{note.individualName || "Unknown"}</span>
                    {note.hasPriorityFlags && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 6, background: "rgba(252,129,129,0.15)", color: "#fc8181", textTransform: "uppercase" }}>Priority</span>}
                    {note.supervisorReviewed && <CheckCircle size={12} style={{ color: "#68d391" }} />}
                    {!note.supervisorReviewed && note.status === "submitted" && <AgeBadge hours={ageHrs} />}
                  </div>
                  <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>
                    {note.agentName ? `${note.agentName} · ` : ""}{note.date} · {note.serviceType} · {note.duration}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <StatusBadge status={note.status} />
                    <span style={{ fontSize: 11, color: "#4fd1c5", fontWeight: 600 }}>{note.billingCode} · {note.billingUnits}</span>
                    {note.confidence && <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: note.confidence >= 90 ? "#68d391" : "#ecc94b" }}>{note.confidence}%</span>}
                    {hasThread && (
                      <button onClick={() => setExpandedNote(isExpanded ? null : note.id)} style={{
                        display: "flex", alignItems: "center", gap: 3, background: "none", border: "none", cursor: "pointer", color: "#63b3ed", fontSize: 10, fontWeight: 600,
                      }}>
                        <MessageSquare size={10} /> {note.messageThread.length}
                        {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                    )}
                  </div>

                  {/* Revision reason inline */}
                  {note.status === "revision-requested" && note.revisionReason && (
                    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8, background: "rgba(246,173,85,0.06)", border: "1px solid rgba(246,173,85,0.1)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#f6ad55", textTransform: "uppercase", marginBottom: 2 }}>Revision Requested by {note.revisionRequestedBy}</div>
                      <div style={{ fontSize: 11, color: "#a0aec0", fontStyle: "italic" }}>{note.revisionReason}</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 12 }}>
                  {note.noteData && (
                    <button style={{ padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "rgba(99,179,237,0.1)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)", cursor: "pointer" }}
                      onClick={() => navigate("/session/review", { state: { note: note.noteData, readOnly: true } })}>View</button>
                  )}
                  {!note.supervisorReviewed && (note.status === "submitted" || note.status === "revision-requested") && (
                    <button style={{ padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer" }}
                      onClick={() => handleSignNote(note.id)}>Sign</button>
                  )}
                  {(note.status === "submitted" || note.status === "revision-requested") && (
                    <button style={{ padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "rgba(246,173,85,0.08)", color: "#f6ad55", border: "1px solid rgba(246,173,85,0.15)", cursor: "pointer" }}
                      onClick={() => setRevisionNote(note)}>
                      {note.status === "revision-requested" ? <><RotateCcw size={10} style={{ display: "inline", marginRight: 3 }} />Re-Revise</> : "Revise"}
                    </button>
                  )}
                  {!hasThread && (note.status === "submitted" || note.status === "signed") && (
                    <button onClick={() => setExpandedNote(isExpanded ? null : note.id)} style={{
                      padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      background: "rgba(99,179,237,0.05)", color: "#718096",
                      border: "1px solid rgba(99,179,237,0.06)", cursor: "pointer",
                    }}>
                      <MessageSquare size={10} style={{ display: "inline", marginRight: 3 }} />Message
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded thread */}
              {isExpanded && (
                <MessageThread thread={note.messageThread || []} noteId={note.id} supervisorName={supervisorName} />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}